import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    type ReactNode,
} from "react";

import { api, ApiError } from "@/api/client";
import { tokenStore } from "@/api/token-store";
import { hasPermission, type Permission } from "../utils/permissions";

export type { Permission };

/**
 * Who is signed in, and what they may do.
 *
 * ONE CONTEXT, THREE AUDIENCES
 * ----------------------------
 * The legacy portal kept an AdminAuthContext and a StudentAuthContext side by
 * side, because it had two unrelated login endpoints. XPSIMS has one identity
 * service and three user types on it, so there is one context and the *type*
 * decides which shell the user lands in. Two contexts would mean two token
 * refresh paths, two logouts, and eventually two answers to "am I logged in".
 *
 * WHAT PERMISSIONS ARE FOR HERE
 * -----------------------------
 * They decide what the interface offers, and nothing more. The gateway and each
 * service enforce access independently; a `can()` that returned true for
 * everything would produce a UI full of buttons that fail with a 403, not a
 * security breach. Treating it as a courtesy rather than a control is what
 * keeps that honest.
 */

export type UserType = "staff" | "student" | "guardian";

export interface AuthUser {
    id: string;
    email: string;
    /** The family name. The API says `surname`, not `lastName`. */
    surname: string;
    firstName: string;
    /** Any remaining names, as one field. */
    otherNames?: string;
    /** School order, as the API assembles it. Never joined client-side. */
    fullName?: string;
    userType: UserType;
    /** The role slug, e.g. `"super_admin"`. Singular — a user holds one role. */
    role: string;
    permissions: Permission[];
    /** Set on a first login or an admin reset; gates the rest of the app. */
    mustChangePassword?: boolean;
    /** Present for student accounts — the student record this login reads. */
    studentId?: string;
}

interface LoginResponse {
    accessToken: string;
    refreshToken: string;
    user: AuthUser;
}

interface AuthContextValue {
    user: AuthUser | null;
    /** True until the boot-time session check has settled. */
    isLoading: boolean;
    /**
     * True from the moment a deliberate sign-out begins.
     *
     * `RequireAuth` reads it to tell "your session ended, come back to where
     * you were" apart from "you chose to leave". Only the first should carry
     * the current page to the sign-in screen; carrying it on a sign-out hands
     * one person's last page to whoever signs in next.
     */
    isSigningOut: boolean;
    /**
     * Signs in. `identifier` is an email, matriculation number, admission
     * number or staff ID — the server decides which.
     */
    login: (identifier: string, password: string) => Promise<AuthUser>;
    logout: () => Promise<void>;
    /** Re-reads /users/me — after a profile edit or a role change. */
    refreshUser: () => Promise<void>;
    can: (resource: string, action: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Restores or establishes the session, and exposes it to the tree.
 *
 * @param props.children - The application
 * @returns The provider
 */
export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<AuthUser | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSigningOut, setSigningOut] = useState(false);

    /**
     * Attempts to restore a session on boot.
     *
     * The access token lives in memory only, so every reload arrives with
     * nothing but a refresh token. `GET /users/me` is what turns that into a
     * session: the client's 401 handling refreshes underneath it transparently,
     * so a single call covers both "token still good" and "token expired but
     * refreshable".
     */
    useEffect(() => {
        let cancelled = false;

        async function restore(): Promise<void> {
            if (!tokenStore.hasSession()) {
                setIsLoading(false);
                return;
            }

            try {
                const me = await api.get<AuthUser>("/users/me");

                if (!cancelled) {
                    setUser(me);
                }
            } catch {
                // Any failure here means no usable session. It is not worth
                // distinguishing an expired token from a revoked one: both end
                // at the login screen, and guessing between them would only
                // change the wording of a message nobody reads.
                tokenStore.clear();
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        }

        void restore();

        return () => {
            cancelled = true;
        };
    }, []);

    /**
     * Drops the user when the token store is emptied from anywhere else.
     *
     * A background query whose refresh fails clears the store directly — it
     * deliberately does not navigate, because a poll failing while the user is
     * reading something should not yank the page out from under them. This
     * subscription is how that clearing becomes a logout the router can see.
     */
    useEffect(() => tokenStore.subscribe(() => {
        if (!tokenStore.hasSession()) {
            setUser(null);
        }
    }), []);

    const login = useCallback(async (identifier: string, password: string) => {
        // `identifier` is whatever the user typed: an email, a matriculation
        // number, an admission number or a staff ID. The server decides which —
        // it is the only party that can, and asking the user to classify
        // themselves before signing in is a question with a wrong answer.
        //
        // `email` is sent alongside for as long as the auth service's
        // LoginRequest requires it, and only when the value actually is one —
        // putting a matric number in a field declared `format: email` earns a
        // 400 that says nothing useful. The day `identifier` is accepted on its
        // own, this drops to one field and nothing else here changes.
        const isEmail = identifier.includes("@");

        const response = await api.post<LoginResponse>(
            "/auth/login",
            isEmail ? { identifier, email: identifier, password } : { identifier, password },
            { anonymous: true }
        );

        tokenStore.set(response.accessToken, response.refreshToken);
        setUser(response.user);
        // Cleared here and not at the end of `logout`, because the sign-out is
        // not over until somebody else has signed in: the flag has to outlive
        // the redirect to /login that follows it.
        setSigningOut(false);
        return response.user;
    }, []);

    const logout = useCallback(async () => {
        // Before the request, not after. The server round trip can take a
        // second, and `RequireAuth` re-renders the instant the token store is
        // cleared — which on a slow network is before this function returns.
        setSigningOut(true);

        try {
            await api.post("/auth/logout");
        } catch (error) {
            // A logout that fails on the server still has to end the session
            // here. Leaving the user apparently signed in because the network
            // dropped is the one outcome that is clearly wrong — the tokens
            // are going away regardless.
            if (!(error instanceof ApiError)) {
                throw error;
            }
        } finally {
            tokenStore.clear();
            setUser(null);
        }
    }, []);

    const refreshUser = useCallback(async () => {
        setUser(await api.get<AuthUser>("/users/me"));
    }, []);

    const can = useCallback(
        (resource: string, action: string) => hasPermission(user?.permissions, resource, action),
        [user]
    );

    const value = useMemo(
        () => ({ user, isLoading, isSigningOut, login, logout, refreshUser, can }),
        [user, isLoading, isSigningOut, login, logout, refreshUser, can]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Reads the current session.
 *
 * @returns The auth context
 * @throws If used outside {@link AuthProvider} — a wiring bug, surfaced loudly
 *   rather than degraded into a silent logged-out state
 */
export function useAuth(): AuthContextValue {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }

    return context;
}
